import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge, Spinner, Alert, Row, Col } from 'reactstrap';
import {
  FaHeart, FaEye, FaShareAlt, FaDownload, FaPhone, FaUsers, FaChartLine,
  FaWhatsapp, FaVideo, FaTag, FaBuilding, FaClock, FaUser, FaEnvelope, FaPlay,
} from 'react-icons/fa';

export const API_BASE_URL = 'https://api.editezy.com/api/admin';

/* ---------- Shared helpers ---------- */
export const secureUrl = (url) =>
  url ? url.replace(/^http:\/\/api\.editezy\.com/, 'https://api.editezy.com') : url;

export const formatDateTime = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export const statusColor = (s) =>
  s === 'approved' ? 'success' : s === 'rejected' ? 'danger' : 'warning';

/* Thumbnail with fallback to first video frame when thumbnailUrl is null */
export const ReelThumb = ({ reel, width = 80, height = 80, onClick, radius = 8 }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const videoSrc = secureUrl(reel.videoUrl);
  const box = {
    position: 'relative', width, height, borderRadius: radius, overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default', background: '#111', flexShrink: 0,
  };
  return (
    <div style={box} onClick={onClick}>
      {reel.thumbnailUrl && !imgFailed ? (
        <img
          src={secureUrl(reel.thumbnailUrl)}
          alt="Reel thumbnail"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <video
          src={videoSrc ? `${videoSrc}#t=0.5` : undefined}
          preload="metadata"
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
        />
      )}
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }}
      >
        <FaPlay className="text-white" size={Math.min(28, Math.max(14, height / 5))} />
      </div>
    </div>
  );
};

/* ---------- Details modal ---------- */
const TABS = [
  { key: 'views', label: 'Views', icon: FaEye },
  { key: 'likes', label: 'Likes', icon: FaHeart },
  { key: 'shares', label: 'Shares', icon: FaShareAlt },
  { key: 'downloads', label: 'Downloads', icon: FaDownload },
  { key: 'whatsappClicks', label: 'WhatsApp', icon: FaWhatsapp },
  { key: 'callClicks', label: 'Calls', icon: FaPhone },
];

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="rd-stat text-center p-2 h-100">
    <Icon className={`text-${color} mb-1`} size={18} />
    <div className="fw-bold fs-5 lh-1">{value ?? 0}</div>
    <small className="text-muted">{label}</small>
  </div>
);

const Info = ({ icon: Icon, label, children }) => (
  <div className="d-flex align-items-start mb-2">
    <Icon className="text-primary mt-1 me-2" />
    <div>
      <small className="text-muted d-block">{label}</small>
      <span className="fw-semibold">{children || 'N/A'}</span>
    </div>
  </div>
);

const ReelDetailsModal = ({ isOpen, toggle, reelId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('views');

  useEffect(() => {
    if (!isOpen || !reelId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setActiveTab('views');
      try {
        const res = await axios.get(`${API_BASE_URL}/reels/${reelId}/details`);
        if (!cancelled) {
          if (res.data.success) setData(res.data);
          else setError(res.data.message || 'Failed to load reel details');
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load reel details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, reelId]);

  const reel = data?.reel;
  const counters = data?.counters || {};
  const grouped = data?.interactions?.grouped || {};
  const uniqueUsers = data?.uniqueUsers || [];
  const activeList = grouped[activeTab] || [];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable centered fullscreen="md">
      <style>{`
        .rd-stat{background:#f8f9fa;border:1px solid #eef0f2;border-radius:10px;transition:all .2s}
        .rd-stat:hover{transform:translateY(-3px);box-shadow:0 6px 14px rgba(0,0,0,.08)}
        .rd-tab{border:0;background:#f1f3f5;border-radius:20px;padding:6px 14px;font-size:.85rem;transition:all .2s;white-space:nowrap}
        .rd-tab:hover{background:#e2e6ea}
        .rd-tab.active{background:#0d6efd;color:#fff}
        .rd-tabs{overflow-x:auto;scrollbar-width:thin}
        .rd-row{border:1px solid #eef0f2;border-radius:10px;transition:background .15s}
        .rd-row:hover{background:#f8f9fa}
      `}</style>

      <ModalHeader toggle={toggle} className="border-0 pb-0">
        <FaVideo className="text-primary me-2" />
        <span className="fw-bold">Reel Details</span>
      </ModalHeader>

      <ModalBody>
        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2 mb-0">Loading details...</p>
          </div>
        )}

        {error && <Alert color="danger">{error}</Alert>}

        {reel && (
          <Row className="g-4">
            {/* Left: video + info */}
            <Col xs={12} lg={5}>
              <video
                key={reel._id}
                src={secureUrl(reel.videoUrl)}
                controls
                playsInline
                poster={secureUrl(reel.thumbnailUrl) || undefined}
                className="w-100 rounded shadow-sm bg-dark"
                style={{ maxHeight: 420, objectFit: 'contain' }}
              />

              <div className="d-flex flex-wrap gap-2 mt-3">
                <Badge color={statusColor(reel.approvalStatus)} className="px-3 py-2 text-capitalize">
                  {reel.approvalStatus}
                </Badge>
                {reel.hotTop && <Badge color="danger" className="px-3 py-2">🔥 Hot</Badge>}
                <Badge color="secondary" className="px-3 py-2">
                  <FaClock className="me-1" /> {reel.duration ?? 0}s
                </Badge>
              </div>

              <div className="mt-3">
                <Info icon={FaBuilding} label="Business">{reel.businessName}</Info>
                <Info icon={FaPhone} label="Phone">{reel.phoneNumber}</Info>
                <Info icon={FaTag} label="Category">{reel.reelCategory?.name}</Info>
                <Info icon={FaClock} label="Created">{formatDateTime(reel.createdAt)}</Info>
                {reel.approvedAt && <Info icon={FaClock} label="Approved at">{formatDateTime(reel.approvedAt)}</Info>}
                {reel.rejectedReason && (
                  <Alert color="danger" className="py-2 mt-2 mb-0">
                    <strong>Rejected reason:</strong> {reel.rejectedReason}
                  </Alert>
                )}
              </div>

              {reel.creatorId && (
                <div className="d-flex align-items-center p-3 mt-3 border rounded">
                  <img
                    src={reel.creatorId.profileImage}
                    alt="creator"
                    width={48}
                    height={48}
                    className="rounded-circle me-3"
                    style={{ objectFit: 'cover', background: '#ddd' }}
                    onError={(e) => { e.target.style.visibility = 'hidden'; }}
                  />
                  <div className="overflow-hidden">
                    <div className="fw-bold text-truncate">{reel.creatorId.name}</div>
                    <small className="text-muted d-block text-truncate">
                      <FaEnvelope className="me-1" />{reel.creatorId.email}
                    </small>
                    <small className="text-muted d-block">
                      <FaPhone className="me-1" />{reel.creatorId.mobile}
                    </small>
                  </div>
                </div>
              )}
            </Col>

            {/* Right: stats + interactions */}
            <Col xs={12} lg={7}>
              <h6 className="fw-bold mb-3"><FaChartLine className="text-primary me-2" />Performance</h6>
              <Row className="g-2 mb-4">
                <Col xs={4} md={3}><StatCard icon={FaEye} label="Views" value={counters.viewCount} color="primary" /></Col>
                <Col xs={4} md={3}><StatCard icon={FaHeart} label="Likes" value={counters.likeCount} color="danger" /></Col>
                <Col xs={4} md={3}><StatCard icon={FaShareAlt} label="Shares" value={counters.shareCount} color="info" /></Col>
                <Col xs={4} md={3}><StatCard icon={FaDownload} label="Downloads" value={counters.downloadCount} color="secondary" /></Col>
                <Col xs={4} md={3}><StatCard icon={FaWhatsapp} label="WhatsApp" value={counters.whatsappClickCount} color="success" /></Col>
                <Col xs={4} md={3}><StatCard icon={FaPhone} label="Calls" value={counters.callClickCount} color="warning" /></Col>
                <Col xs={4} md={3}><StatCard icon={FaUsers} label="Unique Users" value={counters.uniqueUsers} color="primary" /></Col>
                <Col xs={4} md={3}><StatCard icon={FaChartLine} label="Total" value={counters.totalInteractions} color="dark" /></Col>
              </Row>

              <h6 className="fw-bold mb-2">Interactions</h6>
              <div className="rd-tabs d-flex gap-2 pb-2 mb-3">
                {TABS.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    className={`rd-tab ${activeTab === key ? 'active' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    <Icon className="me-1" /> {label} ({(grouped[key] || []).length})
                  </button>
                ))}
              </div>

              {activeList.length === 0 ? (
                <div className="text-center text-muted py-4 border rounded">No interactions in this category yet.</div>
              ) : (
                <div className="d-flex flex-column gap-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {activeList.map((it) => {
                    const u = it.userSnapshot || it.currentUserData || {};
                    return (
                      <div key={it._id} className="rd-row p-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center">
                          <FaUser className="text-muted me-2" />
                          <div>
                            <div className="fw-semibold">{u.name || 'Unknown user'}</div>
                            <small className="text-muted">{u.mobile || u.email}</small>
                          </div>
                        </div>
                        <div className="text-end">
                          <small className="text-muted d-block">{formatDateTime(it.createdAt)}</small>
                          {it.deviceInfo && <small className="text-muted">{it.deviceInfo}</small>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {uniqueUsers.length > 0 && (
                <>
                  <h6 className="fw-bold mt-4 mb-2"><FaUsers className="text-primary me-2" />Unique Users</h6>
                  <div className="d-flex flex-column gap-2">
                    {uniqueUsers.map((u) => (
                      <div key={u.userId} className="rd-row p-2 d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-semibold">{u.user?.name}</div>
                          <small className="text-muted">{u.user?.mobile} • {u.user?.email}</small>
                        </div>
                        <Badge color="primary" pill>{u.totalInteractions} actions</Badge>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Col>
          </Row>
        )}
      </ModalBody>

      <ModalFooter className="border-0 pt-0">
        <Button color="secondary" onClick={toggle} className="px-4">Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReelDetailsModal;